/**
 * Goals & OKRs — company goals, KRAs/KPIs, milestones, check-ins & task linkage.
 * Tabs: Dashboard · Goals (registry + tree) · Timeline (horizon strip)
 *
 * Blended per-goal mode: `okr` (Objective + Key Results) OR `kra_kpi` (KRAs w/ KPIs).
 * Progress modes: manual · kr_weighted · milestone · task.
 * Weekly check-in cadence with confidence flag (on_track / at_risk / off_track).
 * Points: milestone +10, goal complete +50, KR target hit +25, missed check-in −5.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Target, Plus, Search, Edit3, Trash2, TrendingUp, AlertCircle, CheckCircle2, Clock,
  Layers, Flag, ListChecks, MessageSquare, Sparkles, ChevronRight, Send,
} from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/lib/apiError";

const HORIZON_LABELS = {
  vision: "Vision (10–20 yr)",
  long: "Long-term (3–5 yr)",
  annual: "Annual",
  quarterly: "Quarterly",
  monthly: "Monthly",
};

const CONFIDENCE_STYLE = {
  on_track: "border-emerald-500/40 text-emerald-700",
  at_risk: "border-amber-500/40 text-amber-700",
  off_track: "border-red-500/40 text-red-700",
};

const STATUS_STYLE = {
  draft: "border-foreground/25 text-foreground/60",
  active: "border-blue-500/40 text-blue-700",
  completed: "border-emerald-500/40 text-emerald-700",
  cancelled: "border-foreground/25 text-foreground/50",
};


export default function Goals() {
  const { activeWorkspaceId } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "dashboard");
  const [catalog, setCatalog] = useState(null);
  const [openGoalId, setOpenGoalId] = useState(sp.get("open") || null);

  useEffect(() => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      n.set("tab", tab);
      if (!openGoalId) n.delete("open");
      return n;
    }, { replace: true });
  }, [tab, openGoalId, setSp]);
  useEffect(() => { api.get("/goal-catalog").then((r) => setCatalog(r.data)); }, []);

  return (
    <div className="space-y-6" data-testid="goals-page">
      <PageHeader
        title="Goals & OKRs"
        subtitle="10–20 yr vision to weekly check-ins — blend OKRs and KRA/KPIs, link to tasks & leaderboard."
        icon={Target}
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="dashboard" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="goals-tab-dashboard"><Layers className="w-4 h-4 mr-2" />Dashboard</TabsTrigger>
          <TabsTrigger value="registry" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="goals-tab-registry"><Target className="w-4 h-4 mr-2" />Goals</TabsTrigger>
          <TabsTrigger value="timeline" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="goals-tab-timeline"><Flag className="w-4 h-4 mr-2" />Timeline</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-6"><Dashboard wsId={activeWorkspaceId} onOpen={setOpenGoalId} /></TabsContent>
        <TabsContent value="registry" className="mt-6"><Registry wsId={activeWorkspaceId} catalog={catalog} onOpen={setOpenGoalId} /></TabsContent>
        <TabsContent value="timeline" className="mt-6"><Timeline wsId={activeWorkspaceId} onOpen={setOpenGoalId} /></TabsContent>
      </Tabs>
      {openGoalId && <GoalDetailDialog goalId={openGoalId} wsId={activeWorkspaceId} catalog={catalog} onClose={() => setOpenGoalId(null)} />}
    </div>
  );
}


// ─────────────────── Dashboard ───────────────────

function Dashboard({ wsId, onOpen }) {
  const [d, setD] = useState(null);
  const [overdue, setOverdue] = useState([]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/goals/analytics`).then((r) => setD(r.data));
    api.get(`/workspaces/${wsId}/goals?status=active&limit=200`).then((r) => setOverdue((r.data || []).filter((g) => g.checkin_overdue).slice(0, 8)));
  }, [wsId]);
  if (!d) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;
  const s = d.summary;
  return (
    <div className="space-y-5" data-testid="goals-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Target} label="Total goals" value={s.total_goals} testid="goal-stat-total" />
        <Stat icon={TrendingUp} label="Active" value={s.active} testid="goal-stat-active" />
        <Stat icon={CheckCircle2} label="Completed" value={s.completed} testid="goal-stat-completed" tone="ok" />
        <Stat icon={TrendingUp} label="Avg progress" value={`${s.avg_progress_pct}%`} testid="goal-stat-avg" />
        <Stat icon={CheckCircle2} label="On track" value={s.on_track} tone="ok" testid="goal-stat-ontrack" />
        <Stat icon={AlertCircle} label="At risk" value={s.at_risk} tone={s.at_risk ? "warn" : "ok"} testid="goal-stat-atrisk" />
        <Stat icon={AlertCircle} label="Off track" value={s.off_track} tone={s.off_track ? "danger" : "ok"} testid="goal-stat-offtrack" />
        <Stat icon={Clock} label="Overdue check-ins" value={s.overdue_checkins} tone={s.overdue_checkins ? "warn" : "ok"} testid="goal-stat-overdue" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <BarCard title="By horizon" items={d.by_horizon} labelKey="horizon" labelMap={HORIZON_LABELS} testid="goal-by-horizon" />
        <BarCard title="By owner type" items={d.by_owner_type} labelKey="owner_type" testid="goal-by-owner" />
        <BarCard title="By status" items={d.by_status} labelKey="status" testid="goal-by-status" />
        <BarCard title="By confidence" items={d.by_confidence} labelKey="confidence" testid="goal-by-confidence" />
      </div>
      {overdue.length > 0 && (
        <Card className="rounded-none border-amber-500/40">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h4 className="font-bold text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> Overdue check-ins (weekly cadence)</h4>
              <SendDigestButton wsId={wsId} />
            </div>
            <div className="border border-border divide-y divide-border text-sm" data-testid="goals-overdue-list">
              {overdue.map((g) => (
                <button key={g.id} onClick={() => onOpen(g.id)} className="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-foreground/5" data-testid={`overdue-goal-${g.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] uppercase tracking-widest text-foreground/50 shrink-0">{g.horizon}</span>
                    <span className="font-medium truncate">{g.title}</span>
                  </div>
                  <span className="text-xs text-amber-700 font-mono">{Math.round(g.progress_pct || 0)}%</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SendDigestButton({ wsId }) {
  const { can } = useWorkspacePerms();
  const canManage = can("goal.manage");
  const [busy, setBusy] = useState(false);
  if (!canManage) return null;
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/goals/checkin-digest/trigger`);
      const d = r.data || {};
      toast.success(`Digest sent · ${d.recipients} recipient(s), ${d.goals} goal(s)`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to send digest")); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="rounded-none" onClick={send} disabled={busy} data-testid="goals-send-digest">
      <Send className="w-3 h-3 mr-1" />{busy ? "Sending…" : "Send digest now"}
    </Button>
  );
}


function Stat({ icon: Icon, label, value, testid, tone }) {
  const t = { ok: "border-emerald-500/30", warn: "border-amber-500/30", danger: "border-red-500/40" }[tone] || "";
  return (
    <Card className={`rounded-none ${t}`} data-testid={testid}><CardContent className="p-4 flex items-center gap-3">
      <div className="w-9 h-9 border border-border flex items-center justify-center text-foreground/60"><Icon className="w-4 h-4" /></div>
      <div><div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div><div className="text-2xl font-bold leading-none mt-1">{value}</div></div>
    </CardContent></Card>
  );
}

function BarCard({ title, items, labelKey, labelMap, testid }) {
  if (!items?.length) return <Card className="rounded-none"><CardContent className="p-5"><h4 className="font-bold text-sm mb-3">{title}</h4><p className="text-xs text-foreground/55" data-testid={`${testid}-empty`}>No data.</p></CardContent></Card>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <Card className="rounded-none"><CardContent className="p-5">
      <h4 className="font-bold text-sm mb-3">{title}</h4>
      <ul className="space-y-2" data-testid={testid}>{items.map((it) => {
        const key = it[labelKey];
        const label = (labelMap && labelMap[key]) || String(key).replace(/_/g, " ");
        return (
          <li key={key} className="text-sm">
            <div className="flex items-center justify-between text-xs mb-1"><span className="truncate font-medium">{label}</span><span className="text-foreground/60 font-mono">{it.count}</span></div>
            <div className="h-2 bg-foreground/10"><div className="h-full bg-primary" style={{ width: `${(it.count / max) * 100}%` }} /></div>
          </li>
        );
      })}</ul>
    </CardContent></Card>
  );
}


// ─────────────────── Registry ───────────────────

function Registry({ wsId, catalog, onOpen }) {
  const { can } = useWorkspacePerms();
  const canManage = can("goal.manage");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [horizon, setHorizon] = useState("");
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => {
    if (!wsId) return;
    const p = new URLSearchParams();
    if (horizon) p.set("horizon", horizon);
    if (owner) p.set("owner_type", owner);
    if (status) p.set("status", status);
    if (q.trim()) p.set("q", q.trim());
    api.get(`/workspaces/${wsId}/goals?${p.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, horizon, owner, status]);

  return (
    <div className="space-y-4" data-testid="goals-registry">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
          <Input className="pl-9 h-9 rounded-none" placeholder="Search title or description…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} data-testid="goals-search" />
        </div>
        <Select value={horizon || "_all"} onValueChange={(v) => setHorizon(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-52 rounded-none" data-testid="goals-horizon-filter"><SelectValue placeholder="All horizons" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All horizons</SelectItem>{(catalog?.horizons || []).map((h) => <SelectItem key={h} value={h}>{HORIZON_LABELS[h] || h}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={owner || "_all"} onValueChange={(v) => setOwner(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-44 rounded-none" data-testid="goals-owner-filter"><SelectValue placeholder="All owners" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All owners</SelectItem>{(catalog?.owner_types || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status || "_all"} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-40 rounded-none" data-testid="goals-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All statuses</SelectItem>{(catalog?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        {canManage && <Button className="rounded-none" onClick={() => setEditing({})} data-testid="goals-new-btn"><Plus className="w-4 h-4 mr-2" />New goal</Button>}
      </div>

      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="goals-empty">No goals match. {canManage && "Create your first with the button above."}</CardContent></Card>
        : (
          <div className="space-y-2" data-testid="goals-list">
            {rows.map((g) => (
              <button key={g.id} onClick={() => onOpen(g.id)} className="w-full text-left border border-border p-4 hover:bg-foreground/5 transition-colors" data-testid={`goal-row-${g.id}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="rounded-none text-[10px] uppercase tracking-widest">{HORIZON_LABELS[g.horizon] || g.horizon}</Badge>
                      <Badge variant="outline" className={`rounded-none text-[10px] ${STATUS_STYLE[g.status] || ""}`}>{g.status}</Badge>
                      {g.confidence && <Badge variant="outline" className={`rounded-none text-[10px] ${CONFIDENCE_STYLE[g.confidence] || ""}`}>{g.confidence.replace(/_/g, " ")}</Badge>}
                      {g.checkin_overdue && <Badge variant="outline" className="rounded-none text-[10px] border-amber-500/40 text-amber-700"><Clock className="w-3 h-3 mr-1" />Check-in overdue</Badge>}
                      {g.checkin_unresponsive && <Badge variant="outline" className="rounded-none text-[10px] border-red-500/50 text-red-700 bg-red-50/40" data-testid={`goal-unresponsive-${g.id}`}><AlertCircle className="w-3 h-3 mr-1" />Unresponsive</Badge>}
                      {g.mode && <span className="text-[10px] uppercase tracking-widest text-foreground/50">{g.mode}</span>}
                    </div>
                    <div className="font-semibold text-base truncate">{g.title}</div>
                    <div className="text-xs text-foreground/60 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{g.owner_type}</span>
                      {g.owner?.name && <><span>·</span><span>{g.owner.name}</span></>}
                      {g.end_date && <><span>·</span><span>due {g.end_date}</span></>}
                    </div>
                  </div>
                  <div className="w-40 shrink-0">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-foreground/60">Progress</span>
                      <span className="font-mono font-semibold">{Math.round(g.progress_pct || 0)}%</span>
                    </div>
                    <div className="h-2 bg-foreground/10">
                      <div className="h-full bg-primary" style={{ width: `${g.progress_pct || 0}%` }} />
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-foreground/40 shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}

      {editing && <GoalEditor wsId={wsId} goal={editing} catalog={catalog} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}


// ─────────────────── Timeline ───────────────────

function Timeline({ wsId, onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { if (wsId) api.get(`/workspaces/${wsId}/goals?limit=500`).then((r) => setRows(r.data)); }, [wsId]);
  if (!rows) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;
  const grouped = ["vision", "long", "annual", "quarterly", "monthly"].map((h) => ({
    horizon: h, items: rows.filter((r) => r.horizon === h),
  }));
  return (
    <div className="space-y-6" data-testid="goals-timeline">
      {grouped.map((g) => (
        <div key={g.horizon} data-testid={`timeline-${g.horizon}`}>
          <div className="flex items-center gap-2 mb-3">
            <Flag className="w-4 h-4 text-primary" />
            <h3 className="font-bold text-sm uppercase tracking-widest">{HORIZON_LABELS[g.horizon]}</h3>
            <span className="text-xs text-foreground/50">({g.items.length})</span>
          </div>
          {g.items.length === 0
            ? <div className="text-xs text-foreground/50 pl-6" data-testid={`timeline-${g.horizon}-empty`}>No goals in this horizon yet.</div>
            : (
              <div className="pl-6 space-y-2 border-l-2 border-border">
                {g.items.map((it) => (
                  <button key={it.id} onClick={() => onOpen(it.id)} className="w-full text-left -ml-6 pl-6 relative border-l-2 border-transparent hover:border-primary hover:bg-foreground/5 py-2 pr-3 transition-colors" data-testid={`timeline-item-${it.id}`}>
                    <span className="absolute -left-[7px] top-3 w-3 h-3 bg-background border-2 border-primary rounded-full" />
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{it.title}</div>
                        <div className="text-[11px] text-foreground/60">{it.owner?.name || it.owner_type}{it.end_date ? ` · ${it.end_date}` : ""}</div>
                      </div>
                      <span className="text-xs font-mono">{Math.round(it.progress_pct || 0)}%</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
        </div>
      ))}
    </div>
  );
}


// ─────────────────── Goal editor ───────────────────

function GoalEditor({ wsId, goal, catalog, onClose, onSaved }) {
  const isNew = !goal?.id;
  const [f, setF] = useState(() => ({
    title: goal?.title || "",
    description: goal?.description || "",
    horizon: goal?.horizon || "annual",
    owner_type: goal?.owner_type || "individual",
    owner_user_id: goal?.owner_user_id || "",
    owner_department_id: goal?.owner_department_id || "",
    owner_project_id: goal?.owner_project_id || "",
    parent_id: goal?.parent_id || "",
    mode: goal?.mode || "okr",
    progress_mode: goal?.progress_mode || "kr_weighted",
    start_date: goal?.start_date || "",
    end_date: goal?.end_date || "",
    status: goal?.status || "active",
    manual_progress_pct: goal?.manual_progress_pct ?? "",
  }));
  const [members, setMembers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [parents, setParents] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/departments`).then((r) => setDepts(r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/projects`).then((r) => setProjects(r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/goals?limit=200`).then((r) => setParents((r.data || []).filter((x) => x.id !== goal?.id))).catch(() => {});
  }, [wsId, goal?.id]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = { ...f };
      ["owner_user_id", "owner_department_id", "owner_project_id", "parent_id", "start_date", "end_date"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      if (payload.manual_progress_pct === "" || payload.manual_progress_pct === null) payload.manual_progress_pct = null;
      else payload.manual_progress_pct = Number(payload.manual_progress_pct);
      if (isNew) await api.post(`/workspaces/${wsId}/goals`, payload);
      else await api.patch(`/goals/${goal.id}`, payload);
      toast.success(isNew ? "Goal created" : "Goal updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="goal-editor-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New goal" : `Edit ${goal.title}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label="Title *" value={f.title} onChange={(v) => set("title", v)} testid="goal-title" />
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Description</Label>
            <Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-none" data-testid="goal-description" placeholder="Why this goal? What does success look like?" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelF label="Horizon" value={f.horizon} onChange={(v) => set("horizon", v)} options={(catalog?.horizons || []).map((h) => ({ v: h, label: HORIZON_LABELS[h] || h }))} testid="goal-horizon" />
            <SelF label="Status" value={f.status} onChange={(v) => set("status", v)} options={(catalog?.statuses || []).map((s) => ({ v: s, label: s }))} testid="goal-status" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelF label="Mode" value={f.mode} onChange={(v) => set("mode", v)} options={[{ v: "okr", label: "OKR (Objective + Key Results)" }, { v: "kra_kpi", label: "KRA + KPI" }]} testid="goal-mode" />
            <SelF label="Progress computation" value={f.progress_mode} onChange={(v) => set("progress_mode", v)} options={[
              { v: "kr_weighted", label: "Weighted avg of KRs/KPIs" },
              { v: "milestone", label: "Milestone completion" },
              { v: "task", label: "Linked task completion" },
              { v: "manual", label: "Manual %" },
            ]} testid="goal-progress-mode" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelF label="Owner type" value={f.owner_type} onChange={(v) => set("owner_type", v)} options={(catalog?.owner_types || []).map((o) => ({ v: o, label: o }))} testid="goal-owner-type" />
            {f.owner_type === "individual"
              ? <SelF label="Owner" value={f.owner_user_id || "_none"} onChange={(v) => set("owner_user_id", v === "_none" ? "" : v)} options={[{ v: "_none", label: "— unassigned —" }, ...members.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email }))]} testid="goal-owner-user" />
              : f.owner_type === "department"
                ? <SelF label="Department" value={f.owner_department_id || "_none"} onChange={(v) => set("owner_department_id", v === "_none" ? "" : v)} options={[{ v: "_none", label: "— unassigned —" }, ...depts.map((d) => ({ v: d.id, label: d.name }))]} testid="goal-owner-dept" />
                : f.owner_type === "project"
                  ? <SelF label="Project" value={f.owner_project_id || "_none"} onChange={(v) => set("owner_project_id", v === "_none" ? "" : v)} options={[{ v: "_none", label: "— select project —" }, ...projects.map((p) => ({ v: p.id, label: p.name }))]} testid="goal-owner-project" />
                  : <div />}
          </div>
          {f.owner_type === "project" && (
            <div className="border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="project-goal-hint">
              <div className="font-medium mb-1">Project-owned goal</div>
              <p className="text-muted-foreground">Every member of the selected project can see this goal on the project's Goals subtab and post check-ins. Great for delivery OKRs like "Ship v2 by Q3" or "Reduce bug backlog to &lt; 20".</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <F label="Start date" type="date" value={f.start_date} onChange={(v) => set("start_date", v)} testid="goal-start" />
            <F label="End date" type="date" value={f.end_date} onChange={(v) => set("end_date", v)} testid="goal-end" />
          </div>
          <SelF label="Parent goal (rollup)" value={f.parent_id || "_none"} onChange={(v) => set("parent_id", v === "_none" ? "" : v)} options={[{ v: "_none", label: "— none (top-level) —" }, ...parents.map((p) => ({ v: p.id, label: `${HORIZON_LABELS[p.horizon] || p.horizon} · ${p.title}` }))]} testid="goal-parent" />
          {f.progress_mode === "manual" && (
            <F label="Manual progress %" type="number" value={f.manual_progress_pct} onChange={(v) => set("manual_progress_pct", v)} testid="goal-manual-pct" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="goal-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1.5 inline-block">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-none" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
function SelF({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1.5 inline-block">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="h-9 rounded-none" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}


// ─────────────────── Goal detail dialog ───────────────────

export function GoalDetailDialog({ goalId, wsId, catalog, onClose }) {
  const { can } = useWorkspacePerms();
  const { user } = useAuth();
  const canManage = can("goal.manage");
  const [data, setData] = useState(null);
  const [subTab, setSubTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [showNudgeDialog, setShowNudgeDialog] = useState(false);
  const load = () => api.get(`/goals/${goalId}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [goalId]);
  if (!data) return <Dialog open onOpenChange={onClose}><DialogContent><div className="p-4 text-sm">Loading…</div></DialogContent></Dialog>;
  const g = data.goal;
  const isOwner = g.owner_user_id === user?.id;
  const canNudge = !isOwner && !!g.owner_user_id && g.checkin_overdue;
  const nudge = async (reply_by = null) => {
    setNudging(true);
    try {
      await api.post(`/goals/${goalId}/nudge`, { reply_by });
      toast.success(`Nudged ${g.owner?.name || "the owner"} — they'll see a notification.`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to nudge")); }
    finally { setNudging(false); setShowNudgeDialog(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="goal-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Target className="w-4 h-4" />
            <span>{g.title}</span>
            <Badge variant="outline" className="rounded-none text-[10px] uppercase tracking-widest">{HORIZON_LABELS[g.horizon] || g.horizon}</Badge>
            <Badge variant="outline" className={`rounded-none text-[10px] ${STATUS_STYLE[g.status] || ""}`}>{g.status}</Badge>
            {g.confidence && <Badge variant="outline" className={`rounded-none text-[10px] ${CONFIDENCE_STYLE[g.confidence] || ""}`}>{g.confidence.replace(/_/g, " ")}</Badge>}
            {g.checkin_unresponsive && <Badge variant="outline" className="rounded-none text-[10px] border-red-500/50 text-red-700 bg-red-50/40"><AlertCircle className="w-3 h-3 mr-1" />Unresponsive</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-foreground/60">Overall progress · <em className="not-italic">{g.progress_mode?.replace(/_/g, " ")}</em></span>
              <span className="font-mono font-bold text-lg">{Math.round(g.progress_pct || 0)}%</span>
            </div>
            <div className="h-3 bg-foreground/10"><div className="h-full bg-primary" style={{ width: `${g.progress_pct || 0}%` }} /></div>
          </div>
          {canNudge && (
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => setShowNudgeDialog(true)} disabled={nudging} data-testid="goal-detail-nudge" title="Send a check-in reminder to the goal owner">
              <MessageSquare className="w-3 h-3 mr-1" />{nudging ? "…" : "Nudge owner"}
            </Button>
          )}
          {canManage && <Button variant="outline" size="sm" className="rounded-none" onClick={() => setEditing(true)} data-testid="goal-detail-edit"><Edit3 className="w-3 h-3 mr-1" />Edit</Button>}
          {canManage && g.status !== "completed" && (
            <Button size="sm" className="rounded-none" onClick={async () => { const r = await api.patch(`/goals/${goalId}`, { status: "completed" }); celebrate("goal_achieved", { title: g.title || "Goal" }); if (r.data?.points_awarded_this_call) { toast.success(`🏆 Goal completed — +${r.data.points_awarded_this_call} pts!`); } else { toast.success("Goal marked complete"); } load(); }} data-testid="goal-detail-complete"><CheckCircle2 className="w-3 h-3 mr-1" />Complete</Button>
          )}
        </div>

        <Tabs value={subTab} onValueChange={setSubTab}>
          <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
            <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-1.5 text-xs" data-testid="goal-subtab-overview">Overview</TabsTrigger>
            <TabsTrigger value="krs" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-1.5 text-xs" data-testid="goal-subtab-krs">{g.mode === "kra_kpi" ? "KPIs" : "Key Results"} ({data.key_results.length})</TabsTrigger>
            <TabsTrigger value="milestones" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-1.5 text-xs" data-testid="goal-subtab-milestones">Milestones ({data.milestones.length})</TabsTrigger>
            <TabsTrigger value="tasks" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-1.5 text-xs" data-testid="goal-subtab-tasks">Tasks ({data.tasks.length})</TabsTrigger>
            <TabsTrigger value="checkins" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-1.5 text-xs" data-testid="goal-subtab-checkins">Check-ins ({data.checkins.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="space-y-3 text-sm" data-testid="goal-overview">
              {g.description && <div className="border border-border p-3 whitespace-pre-wrap text-foreground/80">{g.description}</div>}
              <div className="grid grid-cols-2 gap-3">
                <DT label="Owner type" value={g.owner_type} />
                <DT label="Owner" value={g.owner?.name || "—"} />
                <DT label="Mode" value={g.mode?.toUpperCase()} />
                <DT label="Progress computation" value={(g.progress_mode || "").replace(/_/g, " ")} />
                <DT label="Start" value={g.start_date || "—"} />
                <DT label="End" value={g.end_date || "—"} />
              </div>
              {data.children.length > 0 && (
                <div>
                  <h4 className="label-eyebrow mb-2">Child goals ({data.children.length})</h4>
                  <div className="border border-border divide-y divide-border" data-testid="goal-children">
                    {data.children.map((c) => (
                      <div key={c.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                        <ChevronRight className="w-3 h-3 text-foreground/40" />
                        <div className="flex-1 min-w-0 truncate">{c.title}</div>
                        <span className="text-xs text-foreground/50">{c.horizon}</span>
                        <span className="font-mono text-xs">{Math.round(c.progress_pct || 0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="krs" className="mt-4"><KRsSection data={data} canManage={canManage} onChange={load} /></TabsContent>
          <TabsContent value="milestones" className="mt-4"><MilestonesSection data={data} canManage={canManage} onChange={load} /></TabsContent>
          <TabsContent value="tasks" className="mt-4"><TasksSection data={data} wsId={wsId} canManage={canManage} onChange={load} /></TabsContent>
          <TabsContent value="checkins" className="mt-4"><CheckinsSection data={data} onChange={load} /></TabsContent>
        </Tabs>

        {editing && <GoalEditor wsId={wsId} goal={g} catalog={catalog} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
        {showNudgeDialog && <NudgeDialog ownerName={g.owner?.name || "owner"} onClose={() => setShowNudgeDialog(false)} onSubmit={nudge} busy={nudging} />}
      </DialogContent>
    </Dialog>
  );
}

function DT({ label, value }) { return <div><div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div><div className="font-medium mt-0.5">{value ?? "—"}</div></div>; }

function NudgeDialog({ ownerName, onClose, onSubmit, busy }) {
  // Default reply-by = tomorrow + 3 days (72h)
  const defaultDate = new Date(Date.now() + 72 * 3600 * 1000).toISOString().slice(0, 10);
  const [replyBy, setReplyBy] = useState(defaultDate);
  const [skip, setSkip] = useState(false);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="nudge-dialog">
        <DialogHeader><DialogTitle>Nudge {ownerName}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-foreground/70">We&apos;ll notify {ownerName} to post a check-in. If they don&apos;t respond by the reply-by date, you&apos;ll get an in-app follow-up.</p>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Reply by</Label>
            <Input type="date" value={replyBy} onChange={(e) => setReplyBy(e.target.value)} disabled={skip} className="h-9 rounded-none" data-testid="nudge-reply-by" />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} data-testid="nudge-skip-followup" />
            Skip follow-up — just send a soft reminder
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={() => onSubmit(skip ? null : new Date(replyBy).toISOString())} disabled={busy} data-testid="nudge-send">
            {busy ? "…" : "Send nudge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




// ─────────────────── KR / KPI section ───────────────────

function KRsSection({ data, canManage, onChange }) {
  const [adding, setAdding] = useState(false);
  const isKPI = data.goal.mode === "kra_kpi";
  const label = isKPI ? "KPI" : "Key Result";
  return (
    <div className="space-y-3" data-testid="goal-krs-section">
      {data.key_results.length === 0 ? <p className="text-xs text-foreground/55">No {label}s yet.</p>
        : (
          <div className="border border-border divide-y divide-border" data-testid="goal-krs-list">
            {data.key_results.map((kr) => (
              <KRRow key={kr.id} kr={kr} canManage={canManage} onChange={onChange} />
            ))}
          </div>
        )}
      {canManage && <Button size="sm" variant="outline" className="rounded-none" onClick={() => setAdding(true)} data-testid="goal-kr-new"><Plus className="w-3 h-3 mr-1" />Add {label}</Button>}
      {adding && <KREditor goalId={data.goal.id} defaultKind={isKPI ? "kpi" : "okr_kr"} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); onChange(); }} />}
    </div>
  );
}

function KRRow({ kr, canManage, onChange }) {
  const [value, setValue] = useState(kr.current_value ?? 0);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/key-results/${kr.id}/update`, { value: Number(value) });
      if (r.data?.points_awarded_this_call) {
        toast.success(`🎯 Target hit — +${r.data.points_awarded_this_call} pts to owner!`);
      } else {
        toast.success("Value updated");
      }
      onChange();
    }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-3 py-2.5" data-testid={`kr-row-${kr.id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{kr.title}</div>
          <div className="text-[11px] text-foreground/55 flex items-center gap-2 flex-wrap">
            <span>{kr.direction}</span>
            <span>·</span>
            <span>start {kr.start_value}{kr.unit ? ` ${kr.unit}` : ""}</span>
            <span>→</span>
            <span>target {kr.target_value}{kr.unit ? ` ${kr.unit}` : ""}</span>
            {kr.weight !== 1 && <><span>·</span><span>weight {kr.weight}</span></>}
          </div>
        </div>
        <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} className="h-8 w-24 rounded-none" data-testid={`kr-value-${kr.id}`} />
        <Button size="sm" className="rounded-none h-8" onClick={submit} disabled={busy} data-testid={`kr-update-${kr.id}`}>Update</Button>
        {canManage && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2" onClick={() => setEditing(true)} data-testid={`kr-edit-${kr.id}`}><Edit3 className="w-3 h-3" /></Button>}
        {canManage && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2 text-destructive" onClick={async () => { if (window.confirm(`Delete "${kr.title}"?`)) { await api.delete(`/key-results/${kr.id}`); toast.success("Deleted"); onChange(); } }} data-testid={`kr-delete-${kr.id}`}><Trash2 className="w-3 h-3" /></Button>}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-foreground/10"><div className="h-full bg-primary" style={{ width: `${kr.progress_pct || 0}%` }} /></div>
        <span className="text-xs font-mono w-10 text-right">{Math.round(kr.progress_pct || 0)}%</span>
      </div>
      {editing && <KREditor kr={kr} defaultKind={kr.kind} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onChange(); }} />}
    </div>
  );
}

function KREditor({ goalId, kr, defaultKind, onClose, onSaved }) {
  const isNew = !kr?.id;
  const [f, setF] = useState(() => ({
    title: kr?.title || "",
    kind: kr?.kind || defaultKind || "okr_kr",
    unit: kr?.unit || "",
    start_value: kr?.start_value ?? 0,
    current_value: kr?.current_value ?? 0,
    target_value: kr?.target_value ?? 0,
    direction: kr?.direction || "increase",
    weight: kr?.weight ?? 1,
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = { ...f };
      ["start_value", "current_value", "target_value", "weight"].forEach((k) => { payload[k] = Number(payload[k]); });
      if (isNew) await api.post(`/goals/${goalId}/key-results`, payload);
      else await api.patch(`/key-results/${kr.id}`, payload);
      toast.success(isNew ? "Added" : "Updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="kr-editor-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add measurement" : "Edit measurement"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label="Title *" value={f.title} onChange={(v) => set("title", v)} testid="kr-title" placeholder="Grow MRR to $100k" />
          <div className="grid grid-cols-3 gap-3">
            <SelF label="Kind" value={f.kind} onChange={(v) => set("kind", v)} options={[{ v: "okr_kr", label: "OKR key result" }, { v: "kpi", label: "KPI" }]} testid="kr-kind" />
            <F label="Unit" value={f.unit} onChange={(v) => set("unit", v)} placeholder="%, $, users" testid="kr-unit" />
            <SelF label="Direction" value={f.direction} onChange={(v) => set("direction", v)} options={[{ v: "increase", label: "Increase" }, { v: "decrease", label: "Decrease" }, { v: "maintain", label: "Maintain" }]} testid="kr-direction" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <F label="Start" type="number" value={f.start_value} onChange={(v) => set("start_value", v)} testid="kr-start" />
            <F label="Current" type="number" value={f.current_value} onChange={(v) => set("current_value", v)} testid="kr-current" />
            <F label="Target *" type="number" value={f.target_value} onChange={(v) => set("target_value", v)} testid="kr-target" />
            <F label="Weight" type="number" value={f.weight} onChange={(v) => set("weight", v)} testid="kr-weight" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="kr-save">{busy ? "…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────── Milestones section ───────────────────

function MilestonesSection({ data, canManage, onChange }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="space-y-3" data-testid="goal-milestones-section">
      {data.milestones.length === 0 ? <p className="text-xs text-foreground/55">No milestones yet.</p>
        : (
          <div className="border border-border divide-y divide-border" data-testid="goal-milestones-list">
            {data.milestones.map((m) => (
              <MilestoneRow key={m.id} m={m} canManage={canManage} onChange={onChange} />
            ))}
          </div>
        )}
      {canManage && <Button size="sm" variant="outline" className="rounded-none" onClick={() => setAdding(true)} data-testid="goal-milestone-new"><Plus className="w-3 h-3 mr-1" />Add milestone</Button>}
      {adding && <MilestoneEditor goalId={data.goal.id} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); onChange(); }} />}
    </div>
  );
}

function MilestoneRow({ m, canManage, onChange }) {
  const [editing, setEditing] = useState(false);
  const done = m.status === "done";
  const generateTask = async () => {
    try {
      const r = await api.post(`/goal-milestones/${m.id}/generate-task`, {});
      toast.success(`Task created: ${r.data.title}`);
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const toggleDone = async () => {
    const r = await api.patch(`/goal-milestones/${m.id}`, { status: done ? "in_progress" : "done" });
    if (!done) celebrate("milestone_completed", { title: m.title || "Milestone" });
    if (r.data?.points_awarded_this_call) {
      toast.success(`🎉 Milestone done — +${r.data.points_awarded_this_call} pts!`);
    } else {
      toast.success(done ? "Reopened" : "Milestone completed!");
    }
    onChange();
  };
  return (
    <div className="px-3 py-2.5 flex items-center gap-3" data-testid={`milestone-row-${m.id}`}>
      <button onClick={toggleDone} className={`w-5 h-5 border ${done ? "bg-primary border-primary" : "border-foreground/40"} flex items-center justify-center shrink-0`} data-testid={`milestone-toggle-${m.id}`}>
        {done && <CheckCircle2 className="w-3.5 h-3.5 text-primary-foreground" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`font-medium text-sm truncate ${done ? "line-through text-foreground/50" : ""}`}>{m.title}</div>
        <div className="text-[11px] text-foreground/55">{m.due_date ? `due ${m.due_date}` : "no date"} · {m.status}{(m.linked_task_ids || []).length ? ` · ${m.linked_task_ids.length} task(s)` : ""}</div>
      </div>
      {canManage && !done && (m.linked_task_ids || []).length === 0 && (
        <Button size="sm" variant="ghost" className="rounded-none h-7 text-[11px] px-2" onClick={generateTask} title="Generate a Task from this milestone" data-testid={`milestone-gen-task-${m.id}`}><ListChecks className="w-3 h-3 mr-1" />→ Task</Button>
      )}
      {canManage && <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setEditing(true)} data-testid={`milestone-edit-${m.id}`}><Edit3 className="w-3 h-3" /></Button>}
      {canManage && <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive" onClick={async () => { if (window.confirm(`Delete "${m.title}"?`)) { await api.delete(`/goal-milestones/${m.id}`); toast.success("Deleted"); onChange(); } }} data-testid={`milestone-delete-${m.id}`}><Trash2 className="w-3 h-3" /></Button>}
      {editing && <MilestoneEditor milestone={m} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onChange(); }} />}
    </div>
  );
}

function MilestoneEditor({ goalId, milestone, onClose, onSaved }) {
  const isNew = !milestone?.id;
  const [f, setF] = useState(() => ({
    title: milestone?.title || "",
    description: milestone?.description || "",
    due_date: milestone?.due_date || "",
    status: milestone?.status || "pending",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = { ...f };
      if (!payload.due_date) payload.due_date = null;
      if (isNew) await api.post(`/goals/${goalId}/milestones`, payload);
      else await api.patch(`/goal-milestones/${milestone.id}`, payload);
      toast.success(isNew ? "Added" : "Updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="milestone-editor-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New milestone" : "Edit milestone"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label="Title *" value={f.title} onChange={(v) => set("title", v)} testid="milestone-title" />
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Description</Label>
            <Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-none" data-testid="milestone-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Due date" type="date" value={f.due_date} onChange={(v) => set("due_date", v)} testid="milestone-due" />
            <SelF label="Status" value={f.status} onChange={(v) => set("status", v)} options={[{ v: "pending", label: "Pending" }, { v: "in_progress", label: "In progress" }, { v: "done", label: "Done" }]} testid="milestone-status" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="milestone-save">{busy ? "…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────── Tasks section ───────────────────

function TasksSection({ data, wsId, canManage, onChange }) {
  const [aiOpen, setAiOpen] = useState(false);
  return (
    <div className="space-y-3" data-testid="goal-tasks-section">
      {data.tasks.length === 0 ? <p className="text-xs text-foreground/55">No linked tasks yet.</p>
        : (
          <div className="border border-border divide-y divide-border" data-testid="goal-tasks-list">
            {data.tasks.map((t) => (
              <div key={t.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`goal-task-${t.id}`}>
                <ListChecks className="w-4 h-4 text-foreground/50" />
                <div className="flex-1 min-w-0"><div className="truncate font-medium">{t.title}</div><div className="text-[10px] text-foreground/55">{t.status} · {t.priority || "medium"}{t.due_date ? ` · due ${t.due_date}` : ""}</div></div>
                {canManage && <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive" onClick={async () => { await api.delete(`/goals/${data.goal.id}/tasks/${t.id}`); toast.success("Unlinked"); onChange(); }} data-testid={`goal-task-unlink-${t.id}`}><Trash2 className="w-3 h-3" /></Button>}
              </div>
            ))}
          </div>
        )}
      {canManage && (
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => setAiOpen(true)} data-testid="goal-ai-suggest-btn"><Sparkles className="w-3 h-3 mr-1" />AI-suggest tasks</Button>
      )}
      {aiOpen && <AiSuggestDialog goalId={data.goal.id} wsId={wsId} defaultAssignee={data.goal.owner_user_id} onClose={() => setAiOpen(false)} onDone={() => { setAiOpen(false); onChange(); }} />}
    </div>
  );
}

function AiSuggestDialog({ goalId, wsId, defaultAssignee, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [context, setContext] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [assignee, setAssignee] = useState(defaultAssignee || "");
  const [members, setMembers] = useState([]);
  const [checked, setChecked] = useState({});
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/projects`).then((r) => setProjects(r.data || [])).catch(() => {});
  }, [wsId]);
  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/goals/${goalId}/ai-suggest-tasks`, { context, max_tasks: 8 });
      const list = r.data.suggestions || [];
      setSuggestions(list);
      const initChecked = {};
      list.forEach((_, i) => { initChecked[i] = true; });
      setChecked(initChecked);
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setBusy(false); }
  };
  const accept = async () => {
    if (!suggestions) return;
    const picks = suggestions.filter((_, i) => checked[i]);
    if (picks.length === 0) { toast.error("Pick at least one task"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/goals/${goalId}/ai-accept-tasks`, { project_id: projectId || null, assignee_id: assignee || null, tasks: picks });
      toast.success(`Created ${r.data.created?.length || 0} task(s)`);
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Accept failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-suggest-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI-suggest tasks</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Extra context (optional)</Label>
            <Textarea rows={2} value={context} onChange={(e) => setContext(e.target.value)} className="rounded-none" placeholder="Anything the AI should know — constraints, current blockers…" data-testid="ai-context" />
          </div>
          {!suggestions
            ? <Button className="rounded-none" onClick={generate} disabled={busy} data-testid="ai-generate">{busy ? "Thinking…" : "Generate suggestions"}</Button>
            : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <SelF label="Project (optional)" value={projectId || "_none"} onChange={(v) => setProjectId(v === "_none" ? "" : v)} options={[{ v: "_none", label: "— none —" }, ...projects.map((p) => ({ v: p.id, label: p.name }))]} testid="ai-project" />
                  <SelF label="Assignee" value={assignee || "_none"} onChange={(v) => setAssignee(v === "_none" ? "" : v)} options={[{ v: "_none", label: "— goal owner —" }, ...members.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email }))]} testid="ai-assignee" />
                </div>
                <div className="border border-border divide-y divide-border" data-testid="ai-suggestions">
                  {suggestions.map((s, i) => (
                    <label key={i} className="px-3 py-2 flex items-center gap-3 cursor-pointer hover:bg-foreground/5" data-testid={`ai-suggestion-${i}`}>
                      <input type="checkbox" checked={!!checked[i]} onChange={(e) => setChecked((c) => ({ ...c, [i]: e.target.checked }))} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{s.title}</div>
                        {s.description && <div className="text-[11px] text-foreground/60">{s.description}</div>}
                      </div>
                      {s.priority && <Badge variant="outline" className="rounded-none text-[10px]">{s.priority}</Badge>}
                    </label>
                  ))}
                </div>
              </>
            )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          {suggestions && <Button className="rounded-none" onClick={accept} disabled={busy} data-testid="ai-accept">{busy ? "…" : `Create ${Object.values(checked).filter(Boolean).length} task(s)`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────── Check-ins section ───────────────────

function CheckinsSection({ data, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3" data-testid="goal-checkins-section">
      {data.checkins.length === 0 ? <p className="text-xs text-foreground/55">No check-ins yet. Post one weekly to keep the team aligned.</p>
        : (
          <div className="border border-border divide-y divide-border" data-testid="goal-checkins-list">
            {data.checkins.map((c) => (
              <div key={c.id} className="px-3 py-3" data-testid={`checkin-row-${c.id}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant="outline" className={`rounded-none text-[10px] ${CONFIDENCE_STYLE[c.confidence] || ""}`}>{c.confidence?.replace(/_/g, " ")}</Badge>
                  <span className="text-xs text-foreground/60">{c.author_name || c.author_user_id}</span>
                  <span className="text-xs text-foreground/50">· {(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  {c.progress_pct != null && <span className="text-xs font-mono ml-auto">{Math.round(c.progress_pct)}%</span>}
                </div>
                {c.narrative && <div className="text-sm text-foreground/80 whitespace-pre-wrap">{c.narrative}</div>}
              </div>
            ))}
          </div>
        )}
      <Button size="sm" className="rounded-none" onClick={() => setOpen(true)} data-testid="goal-checkin-new"><MessageSquare className="w-3 h-3 mr-1" />Post check-in</Button>
      {open && <CheckinEditor goal={data.goal} krs={data.key_results} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); onChange(); }} />}
    </div>
  );
}

function CheckinEditor({ goal, krs, onClose, onSaved }) {
  const [confidence, setConfidence] = useState(goal.confidence || "on_track");
  const [narrative, setNarrative] = useState("");
  const [krVals, setKrVals] = useState(() => Object.fromEntries(krs.map((k) => [k.id, k.current_value ?? 0])));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const kr_updates = krs.filter((k) => Number(krVals[k.id]) !== Number(k.current_value)).map((k) => ({ kr_id: k.id, value: Number(krVals[k.id]) }));
      await api.post(`/goals/${goal.id}/checkins`, { confidence, narrative, kr_updates });
      toast.success("Check-in posted");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="checkin-editor-dialog">
        <DialogHeader><DialogTitle>Weekly check-in</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <SelF label="Confidence" value={confidence} onChange={setConfidence} options={[
            { v: "on_track", label: "On track" }, { v: "at_risk", label: "At risk" }, { v: "off_track", label: "Off track" },
          ]} testid="checkin-confidence" />
          {krs.length > 0 && (
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Update measurements</Label>
              <div className="space-y-2">
                {krs.map((k) => (
                  <div key={k.id} className="flex items-center gap-2 text-sm">
                    <div className="flex-1 truncate">{k.title} <span className="text-[10px] text-foreground/50">(→ {k.target_value}{k.unit ? ` ${k.unit}` : ""})</span></div>
                    <Input type="number" className="h-8 w-24 rounded-none" value={krVals[k.id]} onChange={(e) => setKrVals((v) => ({ ...v, [k.id]: e.target.value }))} data-testid={`checkin-kr-${k.id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Narrative — what happened this week?</Label>
            <Textarea rows={3} value={narrative} onChange={(e) => setNarrative(e.target.value)} className="rounded-none" placeholder="Wins, blockers, plan for next week…" data-testid="checkin-narrative" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="checkin-submit">{busy ? "…" : "Post check-in"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
